import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ThemeMode,
  getInitialTheme,
  getInitialThemePreference,
  resolveTheme,
  setStoredTheme,
} from '../../utils/theme';
import { ThemePreference } from '../../types/user';

/**
 * Theme state carries both what the user chose and what is being rendered
 * (#378).
 *
 * They are separate because `system` has no colour of its own: the preference is
 * what gets saved and shown as selected in settings, while `mode` is what the
 * MUI provider paints. Keeping only one of them loses either the user's choice
 * or the ability to render.
 */
interface ThemeState {
  preference: ThemePreference;
  mode: ThemeMode;
}

const initialState: ThemeState = {
  preference: getInitialThemePreference(),
  // Resolved from the cached preference, so the first frame is already correct
  // and the server's answer confirms rather than corrects it.
  mode: getInitialTheme(),
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    /** The user picked a theme. Cached locally so the next load paints it. */
    setThemePreference: (state, action: PayloadAction<ThemePreference>) => {
      state.preference = action.payload;
      state.mode = resolveTheme(action.payload);
      setStoredTheme(action.payload);
    },

    /**
     * The stored preference arrived from the server. Same effect as choosing it,
     * kept separate so the intent is legible where it is dispatched.
     */
    themeHydrated: (state, action: PayloadAction<ThemePreference>) => {
      state.preference = action.payload;
      state.mode = resolveTheme(action.payload);
      setStoredTheme(action.payload);
    },

    /**
     * The OS colour scheme changed. Only moves the rendered palette, never the
     * preference — the user still chose `system`, and overwriting that would
     * turn a following choice into a fixed one behind their back.
     */
    systemThemeChanged: (state, action: PayloadAction<ThemeMode>) => {
      if (state.preference === 'system') {
        state.mode = action.payload;
      }
    },

    /**
     * The header's light/dark switch. Always lands on an explicit choice: a
     * toggle that could land on `system` would leave the user unable to predict
     * what the next press does.
     */
    toggleTheme: (state) => {
      const next: ThemePreference = state.mode === 'light' ? 'dark' : 'light';
      state.preference = next;
      state.mode = next;
      setStoredTheme(next);
    },
  },
});

export const {
  setThemePreference,
  themeHydrated,
  systemThemeChanged,
  toggleTheme,
} = themeSlice.actions;

export default themeSlice.reducer;
